import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import {
  Absent,
  CLASSIFICATION_LABEL,
  Pill,
  RESPONSE_LABEL,
  classificationTone,
} from '../../components/audit/AuditVocabulary.js';
import { ApiError, apiFetchBlob, describeError } from '../../lib/api.js';
import { FindingForm, findingBody, type FindingDraft } from '../../components/audit/FindingForm.js';
import { resizeImage } from '../../lib/offline/capture.js';
import { uploadFile } from '../../lib/lesson-api.js';
import { useCan, useSession } from '../../lib/session.js';
import { captureGps, describeGps } from '../../lib/offline/capture.js';
import {
  addEvidence,
  answerChecklistItem,
  closeAudit,
  createFinding,
  fetchAudit,
  fetchChecklist,
  fetchChecklistItems,
  fetchChecklistTemplates,
  fetchClosureBlockers,
  fetchEvidence,
  fetchAuditGuests,
  fetchAuditPeople,
  fetchFindings,
  updateAudit,
  updateFinding,
  fetchGuestAccounts,
  inviteGuest,
  recordMinutes,
  revokeGuest,
  type ChecklistResponseValue,
} from '../../lib/audit-api.js';
import { FormPromptPanel } from '../../components/ai/FormPromptPanel.js';
import { disposeFormDraft, requestFindingDraft } from '../../lib/form-prompt-api.js';
import { t, formatDate, formatDateTime } from '../../lib/i18n.js';

/**
 * The audit workspace (spec §11 key screens 4, 5 and 6): the opening and closing
 * minute-taker, the on-site evidence and checklist executor, and the findings
 * and NC classifier.
 *
 * One screen, because on site they are one activity. An auditor works down a
 * checklist, photographs what they see, and writes up the non-conformities as
 * they go; making them navigate between three screens to do it would guarantee
 * that the third step gets left until later, which is precisely how a
 * non-conformity ends up recorded as a dropdown value and nothing else.
 *
 * Two things this screen is careful about:
 *
 *  - **A non-conforming answer is offered a finding immediately**, with the
 *    answer prefilled into the description. Nothing is created without the
 *    auditor pressing the button (ADR-0008's principle, generalised past AI),
 *    but the path of least resistance is the correct one.
 *  - **The closure blockers are shown before the Close button**, not after
 *    pressing it. An auditor should be able to see what is left.
 */
export function AuditWorkspace() {
  const { id } = useParams<{ id: string }>();
  const { user: me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string>('');

  const audit = useQuery({
    queryKey: ['audit', id],
    queryFn: () => fetchAudit(id!),
    enabled: Boolean(id),
  });
  const templates = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: fetchChecklistTemplates,
  });
  const items = useQuery({
    queryKey: ['checklist-items', templateId],
    queryFn: () => fetchChecklistItems(templateId),
    enabled: Boolean(templateId),
  });
  const checklist = useQuery({
    queryKey: ['checklist', id, templateId],
    queryFn: () => fetchChecklist(id!, templateId || undefined),
    enabled: Boolean(id),
  });
  const findings = useQuery({
    queryKey: ['audit-findings', id],
    queryFn: () => fetchFindings(id!),
    enabled: Boolean(id),
  });
  const evidence = useQuery({
    queryKey: ['audit-evidence', id],
    queryFn: () => fetchEvidence(id!),
    enabled: Boolean(id),
  });
  // Offered only to those who hold them somewhere; the server still decides
  // every request against this audit (pass 9).
  const canEdit = useCan('audit', 'create_edit');
  const canSubmit = useCan('audit', 'submit');
  const canClose = canEdit && canSubmit;

  const blockers = useQuery({
    queryKey: ['audit-closure', id, templateId],
    queryFn: () => fetchClosureBlockers(id!, templateId || undefined),
    enabled: Boolean(id),
  });

  const refresh = () => {
    for (const key of [
      'audit',
      'checklist',
      'audit-findings',
      'audit-evidence',
      'audit-closure',
    ]) {
      void queryClient.invalidateQueries({ queryKey: [key, id] });
    }
    void queryClient.invalidateQueries({ queryKey: ['checklist', id, templateId] });
    void queryClient.invalidateQueries({ queryKey: ['audit-closure', id, templateId] });
  };

  const fail = (fallback: string) => (caught: unknown) =>
    setError(describeError(caught, fallback));

  const minutes = useMutation({
    mutationFn: (body: { meeting: 'opening' | 'closing'; heldAt: string; minutes: string }) =>
      recordMinutes(id!, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("Those minutes could not be recorded.")),
  });

  const answer = useMutation({
    mutationFn: (body: {
      templateItemId: string;
      response: ChecklistResponseValue;
      notes?: string;
    }) => answerChecklistItem(id!, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("That answer could not be saved.")),
  });

  /** The finding being raised or edited (FN-8). */
  const [findingForm, setFindingForm] = useState<
    | { mode: 'new'; checklistResponseId?: string; initial: FindingDraft; revision?: number }
    | { mode: 'edit'; findingId: string; initial: FindingDraft }
    | null
  >(null);

  /** An AI draft applied to the finding form, until it is raised or discarded (pass 8). */
  const [aiDraft, setAiDraft] = useState<{ suggestionId: string; answers: Record<string, string> } | null>(null);

  /** Put drafted values into the new-finding form; it is remounted to show them. */
  const applyToFinding = (values: Record<string, string>) =>
    setFindingForm((current) =>
      current?.mode === 'new'
        ? {
            ...current,
            initial: { ...current.initial, ...(values as Partial<FindingDraft>) } as FindingDraft,
            revision: (current.revision ?? 0) + 1,
          }
        : current,
    );

  const raise = useMutation({
    mutationFn: (body: Record<string, unknown>) => createFinding(id!, body),
    onSuccess: (result) => {
      setError(null);
      setFindingForm(null);
      // The server decides accepted or edited from the finding as raised.
      if (aiDraft) {
        void disposeFormDraft('audit', aiDraft.suggestionId, {
          claimed: 'accepted',
          recordId: result.finding.id,
          answers: aiDraft.answers,
        }).catch(() => undefined);
        setAiDraft(null);
      }
      refresh();
    },
    onError: fail(t("That finding could not be raised.")),
  });

  const reclassify = useMutation({
    mutationFn: ({ findingId, body }: { findingId: string; body: Record<string, unknown> }) => updateFinding(findingId, body),
    onSuccess: () => {
      setError(null);
      setFindingForm(null);
      refresh();
    },
    onError: fail(t("That finding could not be changed.")),
  });

  const attach = useMutation({
    mutationFn: (body: Record<string, unknown>) => addEvidence(id!, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("That evidence could not be saved.")),
  });

  const close = useMutation({
    mutationFn: () => closeAudit(id!, templateId || undefined),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("This audit could not be closed.")),
  });

  const record = audit.data?.audit;
  const responses = checklist.data?.responses ?? [];
  const progress = checklist.data?.progress;
  const answerFor = (itemId: string) => responses.find((row) => row.templateItemId === itemId);

  if (!id) return null;
  // A Guest Auditor reads; this screen is for doing the audit.
  if (me?.platformRole === 'guest_auditor') return <Navigate to={`/audit/invited/${id}`} replace />;

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audits', record?.referenceNo ?? '…']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        {audit.isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}
        {audit.error && <p className="text-tone-critical">{t("That audit could not be loaded.")}</p>}

        {record && (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h1 className="m-0 text-2xl font-semibold">{record.title}</h1>
              <Pill tone={record.closedAt ? 'positive' : 'neutral'}>
                {record.phase.replace('_', '-')} · {record.status.replace('_', ' ')}
              </Pill>
            </div>
            <p className="mt-1 text-sm text-content-secondary">{record.referenceNo}</p>
            <p className="mt-1 text-sm">
              <Link to={`/audit/audits/${record.id}/report`}>{t("Audit report preview")}</Link>
            </p>

            {error && (
              <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
                {error}
              </p>
            )}

            <TeamPanel
              auditId={record.id}
              phase={record.phase}
              canEdit={canEdit}
              leadAuditorId={record.leadAuditorId}
              teamUserIds={record.teamUserIds}
              onChanged={refresh}
            />

            {canEdit && (
              <MeetingSection
                audit={record}
                onRecord={(body) => minutes.mutate(body)}
                pending={minutes.isPending}
              />
            )}

            {/* --- Checklist executor ------------------------------------- */}
            <section className="mt-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-lg font-semibold">{t("Checklist")}</h2>
                <label className="text-sm">
                  <span className="me-2 text-content-secondary">{t("Template")}</span>
                  <select
                    className="rounded border border-border px-2 py-1"
                    value={templateId}
                    onChange={(event) => setTemplateId(event.target.value)}
                  >
                    <option value="">{t("Choose a checklist…")}</option>
                    {(templates.data?.templates ?? []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {progress && templateId && (
                <p className="mt-2 text-sm text-content-secondary">
                  {progress.answered}{' '}{t("of")}{' '}{progress.total}{' '}{t("answered")}
                  {progress.nonConformingWithoutFinding > 0 && (
                    <>
                      {' · '}
                      <strong className="text-tone-critical">
                        {progress.nonConformingWithoutFinding}{' '}{t("non-conforming with no finding raised")}
                      </strong>
                    </>
                  )}
                </p>
              )}

              {!templateId && (
                <p className="mt-3 rounded-md border border-border bg-surface p-4 text-sm text-content-secondary">
                  {t("Choose a checklist to work through. Answers are only accepted while the audit is in execution — between the opening and closing meetings.")}
                </p>
              )}

              <ul className="mt-4 list-none space-y-3 p-0">
                {(items.data?.items ?? []).map((item) => (
                  <ChecklistRow
                    key={item.id}
                    clause={item.clause}
                    question={item.question}
                    guidance={item.guidance}
                    current={answerFor(item.id)}
                    disabled={record.phase !== 'execution' || answer.isPending || !canEdit}
                    onAnswer={(response, notes) =>
                      answer.mutate({ templateItemId: item.id, response, notes })
                    }
                    onRaiseFinding={(description, checklistResponseId) =>
                      // The auditor classifies it and chooses the risk — no
                      // fixed "Minor NC, Medium" any more (FN-8).
                      setFindingForm({
                        mode: 'new',
                        checklistResponseId,
                        initial: {
                          classification: 'minor_nc',
                          riskLevel: '',
                          clause: item.clause ?? '',
                          description,
                          evidenceSummary: '',
                        },
                      })
                    }
                  />
                ))}
              </ul>
            </section>

            <EvidenceSection
              evidence={evidence.data?.evidence ?? []}
              disabled={Boolean(record.closedAt) || attach.isPending || !canEdit}
              onAttach={(body) => attach.mutate(body)}
              onError={setError}
            />

            {/* --- Findings ----------------------------------------------- */}
            <section className="mt-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-lg font-semibold">{t("Findings")}</h2>
                {record.phase === 'execution' && !record.closedAt && !findingForm && canEdit && (
                  <button
                    type="button"
                    className="rounded border border-border px-3 py-1.5 text-sm"
                    onClick={() =>
                      setFindingForm({
                        mode: 'new',
                        initial: { classification: 'observation', riskLevel: '', clause: '', description: '', evidenceSummary: '' },
                      })
                    }
                  >
                    {t("Raise a finding")}
                  </button>
                )}
              </div>
              {findingForm?.mode === 'new' && !findingForm.checklistResponseId && (
                <FormPromptPanel
                  id="finding-prompt"
                  heading={t("Describe the finding in a sentence")}
                  label={t("Describe the finding")}
                  placeholder={t("e.g. Minor non-conformity against clause 7.5.3, medium risk: superseded drawings in use at level 3.")}
                  request={(prompt) => requestFindingDraft(record.id, prompt)}
                  onApply={(result) => {
                    applyToFinding(result.fields);
                    setAiDraft({ suggestionId: result.suggestionId!, answers: {} });
                  }}
                  onAnswer={(field, value) => {
                    applyToFinding({ [field]: value });
                    setAiDraft((current) => (current ? { ...current, answers: { ...current.answers, [field]: value } } : current));
                  }}
                  onDiscard={() => {
                    if (aiDraft) void disposeFormDraft('audit', aiDraft.suggestionId, { claimed: 'rejected' }).catch(() => undefined);
                    setAiDraft(null);
                  }}
                />
              )}
              {findingForm && (
                <FindingForm
                  key={
                    findingForm.mode === 'edit'
                      ? findingForm.findingId
                      : `${findingForm.checklistResponseId ?? 'new'}-${findingForm.revision ?? 0}`
                  }
                  initial={findingForm.initial}
                  heading={findingForm.mode === 'edit' ? t("Edit the finding") : t("Raise a finding")}
                  submitLabel={findingForm.mode === 'edit' ? t("Save the finding") : t("Raise it")}
                  busy={raise.isPending || reclassify.isPending}
                  onCancel={() => {
                    if (aiDraft) void disposeFormDraft('audit', aiDraft.suggestionId, { claimed: 'rejected' }).catch(() => undefined);
                    setAiDraft(null);
                    setFindingForm(null);
                  }}
                  onSubmit={(draft) =>
                    findingForm.mode === 'edit'
                      ? reclassify.mutate({ findingId: findingForm.findingId, body: findingBody(draft) })
                      : raise.mutate({
                          ...findingBody(draft),
                          ...(findingForm.checklistResponseId ? { checklistResponseId: findingForm.checklistResponseId } : {}),
                        })
                  }
                />
              )}
              {(findings.data?.findings ?? []).length === 0 && (
                <p className="mt-2 text-sm text-content-secondary">{t("Nothing raised yet.")}</p>
              )}
              <ul className="mt-3 list-none space-y-2 p-0">
                {(findings.data?.findings ?? []).map((finding) => (
                  <li
                    key={finding.id}
                    className="rounded-md border border-border bg-surface p-4 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <strong>{finding.referenceNo}</strong>
                      <Pill tone={classificationTone(finding.classification)}>
                        {t(CLASSIFICATION_LABEL[finding.classification])}
                      </Pill>
                    </div>
                    <p className="mt-1 mb-0">{finding.description}</p>
                    <p className="mt-1 mb-0 text-content-secondary">
                      {t("Risk:")}{' '}
                      {finding.riskLevel ?? (
                        <Absent reason={t("Only a non-conformity carries a risk level.")} />
                      )}
                      {' · '}
                      <Link to={`/audit/findings?auditId=${record.id}`}>{t("Corrective actions")}</Link>
                    </p>
                    {!finding.closedAt && !record.closedAt && canEdit && (
                      <button
                        type="button"
                        className="mt-2 rounded border border-border px-2 py-0.5 text-xs"
                        onClick={() =>
                          setFindingForm({
                            mode: 'edit',
                            findingId: finding.id,
                            initial: {
                              classification: finding.classification,
                              riskLevel: finding.riskLevel ?? '',
                              clause: finding.clause ?? '',
                              description: finding.description,
                              evidenceSummary: finding.evidenceSummary ?? '',
                            },
                          })
                        }
                      >
                        {t("Edit")}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {canEdit && <GuestAuditors auditId={record.id} closed={Boolean(record.closedAt)} />}

            {/* --- Closure ------------------------------------------------ */}
            <section className="mt-8 rounded-md border border-border bg-surface p-5">
              <h2 className="m-0 text-lg font-semibold">{t("Close this audit")}</h2>
              {record.closedAt ? (
                <p className="mt-2 mb-0 text-sm text-content-secondary">
                  {t("Closed")}{' '}{formatDate(record.closedAt)}{t(". No Guest Auditor can read it any more: their access ended when it closed.")}
                </p>
              ) : (blockers.data?.blockers ?? []).length > 0 ? (
                <>
                  <p className="mt-2 text-sm text-content-secondary">
                    {t("Still outstanding — all of it, not just the first:")}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 ps-5 text-sm">
                    {blockers.data!.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </>
              ) : !canClose ? (
                <p className="mt-2 mb-0 text-sm text-content-secondary">
                  {t("Nothing is outstanding. The audit team closes it.")}
                </p>
              ) : (
                <button
                  type="button"
                  className="mt-3 rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
                  onClick={() => close.mutate()}
                  disabled={close.isPending}
                >
                  {t("Close the audit")}
                </button>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function MeetingSection({
  audit,
  onRecord,
  pending,
}: {
  audit: { openingMeetingAt: string | null; closingMeetingAt: string | null; phase: string };
  onRecord: (body: { meeting: 'opening' | 'closing'; heldAt: string; minutes: string }) => void;
  pending: boolean;
}) {
  const [meeting, setMeeting] = useState<'opening' | 'closing'>('opening');
  const [heldAt, setHeldAt] = useState('');
  const [text, setText] = useState('');

  const recorded = meeting === 'opening' ? audit.openingMeetingAt : audit.closingMeetingAt;

  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5">
      <h2 className="m-0 text-lg font-semibold">{t("Meeting minutes")}</h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("The time the meeting was")}{' '}<strong>{t("held")}</strong>{t(", not the time you write this up. Minutes are typed afterwards, and a wrong time is a wrong fact in an audit record.")}
      </p>

      <div className="mt-3 flex gap-2">
        {(['opening', 'closing'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`rounded px-3 py-1.5 text-sm ${
              meeting === value ? 'bg-ag-purple text-white' : 'border border-border'
            }`}
            onClick={() => setMeeting(value)}
          >
            {value === 'opening' ? t("Opening") : t("Closing")}
          </button>
        ))}
      </div>

      {recorded && (
        <p className="mt-3 mb-0 text-sm text-content-secondary">
          {t("Recorded")}{' '}{formatDateTime(recorded)}.
        </p>
      )}

      <form
        className="mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          onRecord({ meeting, heldAt: new Date(heldAt).toISOString(), minutes: text });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-content-secondary">{t("Held at")}</span>
            <input
              type="datetime-local"
              className="rounded border border-border px-2 py-1"
              value={heldAt}
              onChange={(event) => setHeldAt(event.target.value)}
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-content-secondary">{t("Minutes")}</span>
            <textarea
              className="rounded border border-border px-2 py-1"
              rows={3}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
        </div>
        <button
          type="submit"
          className="mt-3 rounded border border-border px-3 py-1.5 text-sm"
          disabled={pending || !heldAt || !text.trim()}
        >
          {t("Record")}{' '}{meeting}{' '}{t("meeting")}
        </button>
      </form>
    </section>
  );
}

function ChecklistRow({
  clause,
  question,
  guidance,
  current,
  disabled,
  onAnswer,
  onRaiseFinding,
}: {
  clause: string | null;
  question: string;
  guidance: string | null;
  current?: { id: string; response: ChecklistResponseValue; notes: string | null; findingId: string | null };
  disabled: boolean;
  onAnswer: (response: ChecklistResponseValue, notes?: string) => void;
  onRaiseFinding: (description: string, checklistResponseId: string) => void;
}) {
  const [notes, setNotes] = useState(current?.notes ?? '');
  const response = current?.response ?? 'unanswered';

  return (
    <li className="rounded-md border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="m-0 text-sm font-medium">
          {clause && <span className="text-content-secondary">{clause} · </span>}
          {question}
        </p>
        <Pill tone={response === 'does_not_conform' ? 'critical' : 'neutral'}>
          {t(RESPONSE_LABEL[response])}
        </Pill>
      </div>
      {guidance && <p className="mt-1 mb-0 text-xs text-content-secondary">{guidance}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {(['conforms', 'does_not_conform', 'not_applicable'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className="rounded border border-border px-3 py-1 text-sm"
            disabled={disabled || (value === 'does_not_conform' && !notes.trim())}
            onClick={() => onAnswer(value, notes || undefined)}
          >
            {t(RESPONSE_LABEL[value])}
          </button>
        ))}
      </div>

      <label className="mt-2 flex flex-col text-sm">
        <span className="mb-1 text-content-secondary">
          {t("What you observed — required to mark this non-conforming")}
        </span>
        <textarea
          className="rounded border border-border px-2 py-1"
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {/* The nudge that closes the gap: a non-conformity with no finding is the
          most important thing the auditor saw, surviving only as a dropdown. */}
      {current && response === 'does_not_conform' && !current.findingId && (
        <div className="mt-3 rounded border border-border bg-tone-info-surface p-3">
          <p className="m-0 text-sm">
            {t("This is recorded as non-conforming but no finding has been raised. The audit cannot be closed until one is.")}
          </p>
          <button
            type="button"
            className="mt-2 rounded bg-ag-purple px-3 py-1.5 text-sm text-white"
            onClick={() => onRaiseFinding(current.notes ?? question, current.id)}
          >
            {t("Raise a finding from this")}
          </button>
        </div>
      )}
    </li>
  );
}

function EvidenceSection({
  evidence,
  disabled,
  onAttach,
  onError,
}: {
  evidence: {
    id: string;
    evidenceType: string;
    description: string | null;
    fileId?: string | null;
    gpsCaptureStatus: 'captured' | 'denied' | 'unavailable' | 'timed_out';
    gpsLatitude: string | null;
    gpsLongitude: string | null;
    capturedAt: string | null;
  }[];
  disabled: boolean;
  onAttach: (body: Record<string, unknown>) => void;
  onError: (message: string) => void;
}) {
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * A photograph or a document seen on site (FN-10). A photo is made smaller on
   * the device first, as lesson photos are; a document is a PDF. The file is
   * uploaded, then recorded as evidence with the device's location.
   */
  const attachFile = async (file: File) => {
    setBusy(true);
    try {
      const isPdf = file.type === 'application/pdf';
      const prepared = isPdf
        ? { data: await readAsDataUrl(file), contentType: 'application/pdf' }
        : await resizeImage(file);
      const stored = await uploadFile({
        filename: isPdf ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg',
        contentType: prepared.contentType,
        data: prepared.data,
        purpose: 'audit_evidence',
      });
      const fix = await captureGps();
      onAttach({
        evidenceType: isPdf ? 'document' : 'photo',
        description: description.trim() || file.name,
        fileId: stored.id,
        gpsCaptureStatus: fix.status,
        gpsLatitude: fix.latitude,
        gpsLongitude: fix.longitude,
        capturedAt: new Date().toISOString(),
      });
      setDescription('');
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : t("That file could not be attached."));
    } finally {
      setBusy(false);
    }
  };

  /**
   * GPS is captured by the device, never typed (spec §11), and a failed fix
   * records **why** rather than leaving a blank. An auditor cannot be asked to
   * defend evidence whose location is blank for unknown reasons.
   */
  const attach = async () => {
    setBusy(true);
    const fix = await captureGps();
    onAttach({
      evidenceType: 'observation',
      description,
      gpsCaptureStatus: fix.status,
      gpsLatitude: fix.latitude,
      gpsLongitude: fix.longitude,
      capturedAt: new Date().toISOString(),
    });
    setDescription('');
    setBusy(false);
  };

  return (
    <section className="mt-8">
      <h2 className="m-0 text-lg font-semibold">{t("Evidence")}</h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("Location is captured by the device. If it cannot get a fix, the reason is recorded rather than left blank.")}
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("What you observed")}</span>
          <input
            className="rounded border border-border px-2 py-1"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 text-sm"
          disabled={disabled || busy || !description.trim()}
          onClick={() => void attach()}
        >
          {busy ? t("Working…") : t("Record observation")}
        </button>
        <label className="rounded border border-border px-3 py-1.5 text-sm">
          {t("Attach a photo or PDF")}
          <input
            type="file"
            accept="image/*,application/pdf"
            className="sr-only"
            disabled={disabled || busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void attachFile(file);
            }}
          />
        </label>
      </div>

      <ul className="mt-4 list-none space-y-2 p-0">
        {evidence.map((item) => (
          <li key={item.id} className="rounded-md border border-border bg-surface p-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                {item.evidenceType !== 'observation' && <span className="capitalize">{item.evidenceType} · </span>}
                {item.description ?? item.evidenceType}
                {item.fileId && <OpenEvidenceFile fileId={item.fileId} />}
              </span>
              <span className="text-xs text-content-secondary">
                {describeGps(item)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Guest Auditors (spec §6, SEC-8): external auditors invited to read this one
 * audit, until an end date or its close. Shown to whoever can edit the audit —
 * the list itself is refused to anyone else, so the panel simply does not
 * appear for them.
 */
function GuestAuditors({ auditId, closed }: { auditId: string; closed: boolean }) {
  const queryClient = useQueryClient();
  const guests = useQuery({ queryKey: ['audit-guests', auditId], queryFn: () => fetchAuditGuests(auditId), retry: false });
  const accounts = useQuery({ queryKey: ['guest-accounts'], queryFn: fetchGuestAccounts, retry: false, enabled: !closed });
  const [userId, setUserId] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['audit-guests', auditId] });
  };
  const invite = useMutation({
    // The end of the chosen day, in the reader's own time.
    mutationFn: () => inviteGuest(auditId, { userId, expiresAt: new Date(`${until}T23:59:59`).toISOString() }),
    onSuccess: () => {
      setUserId('');
      setUntil('');
      done();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The guest could not be invited.")),
  });
  const revoke = useMutation({
    mutationFn: (accessId: string) => revokeGuest(auditId, accessId),
    onSuccess: done,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The invitation could not be revoked.")),
  });

  if (guests.error || !guests.data) return null;
  const now = Date.now();
  const rows = guests.data.guests;

  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5" aria-labelledby="guests-heading">
      <h2 id="guests-heading" className="m-0 text-lg font-semibold">{t("Guest Auditors")}</h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("An external auditor invited here can read this audit — and nothing else — until the end date, or until the audit closes. Every page they open is recorded. Their account is created in Users & roles.")}
      </p>

      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-content-secondary">{t("Nobody is invited.")}</p>
      ) : (
        <ul className="mt-3 list-none space-y-2 p-0 text-sm">
          {rows.map((row) => {
            const state = row.revokedAt
              ? 'revoked'
              : closed
                ? 'ended with the audit'
                : new Date(row.expiresAt).getTime() <= now
                  ? 'ended'
                  : `until ${formatDate(row.expiresAt)}`;
            const live = !row.revokedAt && !closed && new Date(row.expiresAt).getTime() > now;
            return (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
                <span>
                  <strong>{row.displayName}</strong> <span className="text-content-secondary">{row.email}</span> · {state}
                </span>
                {live && (
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-0.5 text-xs"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(row.id)}
                  >
                    {t("Revoke")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!closed && (
        <form
          aria-label={t("Invite a Guest Auditor")}
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            invite.mutate();
          }}
        >
          <label className="text-sm">
            {t("Guest Auditor")}
            <select
              className="mt-1 block rounded border border-border px-2 py-1.5"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              required
            >
              <option value="">{t("Choose an account")}</option>
              {(accounts.data?.guests ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.displayName} ({account.email})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            {t("Until")}
            <input
              type="date"
              className="mt-1 block rounded border border-border px-2 py-1.5"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              required
            />
          </label>
          <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60" disabled={invite.isPending}>
            {t("Invite")}
          </button>
        </form>
      )}
      {(accounts.data?.guests.length ?? 1) === 0 && !closed && (
        <p className="mt-2 text-xs text-content-secondary">
          {t("There are no Guest Auditor accounts yet. An access administrator creates one in Users & roles.")}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error(t("The file could not be read.")));
    reader.readAsDataURL(file);
  });
}

/** Opened with the sign-in token, then shown: a plain link would carry none. */
function OpenEvidenceFile({ fileId }: { fileId: string }) {
  return (
    <button
      type="button"
      className="ms-2 text-xs text-ag-purple underline"
      onClick={() => {
        void apiFetchBlob(`/files/${fileId}`).then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener');
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        });
      }}
    >
      {t("Open")}
    </button>
  );
}

/**
 * The lead auditor and the team (FN-10). Set while the audit is in pre-audit —
 * the API refuses changes after that — and shown by name afterwards.
 */
function TeamPanel({
  auditId,
  phase,
  canEdit,
  leadAuditorId,
  teamUserIds,
  onChanged,
}: {
  auditId: string;
  phase: string;
  canEdit: boolean;
  leadAuditorId: string | null;
  teamUserIds: string[];
  onChanged: () => void;
}) {
  const people = useQuery({ queryKey: ['audit-people'], queryFn: fetchAuditPeople, retry: false });
  const [lead, setLead] = useState(leadAuditorId ?? '');
  const [team, setTeam] = useState<string[]>(teamUserIds);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => updateAudit(auditId, { leadAuditorId: lead || null, teamUserIds: team }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The team could not be saved.")),
  });
  const list = people.data?.people ?? [];
  const nameOf = (id: string) => list.find((person) => person.id === id)?.displayName ?? 'Someone';
  const editable = canEdit && phase === 'pre_audit' && !people.error;

  return (
    <section className="mt-6 rounded-md border border-border bg-surface p-4 text-sm" aria-labelledby="team-heading">
      <h2 id="team-heading" className="m-0 text-base font-semibold">{t("Lead auditor and team")}</h2>
      {!editable ? (
        <p className="mb-0 mt-2">
          {leadAuditorId ? <>{t("Lead:")}{' '}<strong>{nameOf(leadAuditorId)}</strong>. </> : t("No lead auditor recorded. ")}
          {teamUserIds.length ? t("Team: {0}.", { 0: teamUserIds.map(nameOf).join(', ') }) : t("No team recorded.")}
          {phase !== 'pre_audit' && t(" The team is set before the opening meeting.")}
        </p>
      ) : (
        <form
          aria-label={t("Lead auditor and team")}
          className="mt-2 grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Lead auditor")}</span>
            <select className="max-w-sm rounded border border-border-strong px-2 py-1.5" value={lead} onChange={(event) => setLead(event.target.value)}>
              <option value="">{t("Not chosen")}</option>
              {list.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                  {person.designation ? ` — ${person.designation}` : ''}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="m-0 rounded border border-border p-2">
            <legend className="px-1 text-content-secondary">{t("Team")}</legend>
            <div className="grid gap-1 sm:grid-cols-2">
              {list.map((person) => (
                <label key={person.id} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={team.includes(person.id)}
                    onChange={() =>
                      setTeam(team.includes(person.id) ? team.filter((each) => each !== person.id) : [...team, person.id])
                    }
                  />
                  {person.displayName}
                </label>
              ))}
            </div>
          </fieldset>
          {error && (
            <p role="alert" className="m-0" style={{ color: 'var(--tone-critical)' }}>
              {error}
            </p>
          )}
          <button type="submit" className="w-fit rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60" disabled={save.isPending}>
            {t("Save the team")}
          </button>
        </form>
      )}
    </section>
  );
}
